/**
 * Olfacy notes library: instant search, sort and "Trending" on the rendered note cards (no requests),
 * plus the mobile Sort / Filter Notes sheets. Family & sub-category navigation are normal links.
 */
class OlfacyNotes extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-grid]');
    if (!this.grid) return;
    this.fixed = [...this.grid.querySelectorAll(':scope > [data-fixed]')]; // e.g. "All Citrus" — always first
    this.items = [...this.grid.children].filter((el) => !el.hasAttribute('data-fixed'));
    this.state = { query: '', trending: false, sort: 'popular' };
    this.count = this.querySelector('[data-count]');
    this.empty = this.querySelector('[data-empty]');
    this.abort = new AbortController();
    const { signal } = this.abort;

    let timer;
    this.querySelector('[data-search]')?.addEventListener('input', (e) => {
      clearTimeout(timer);
      timer = setTimeout(() => { this.state.query = e.target.value.trim().toLowerCase(); this.apply(); }, 120);
    }, { signal });

    this.querySelector('[data-trending]')?.addEventListener('click', (e) => {
      this.state.trending = !this.state.trending;
      e.currentTarget.setAttribute('aria-pressed', String(this.state.trending));
      this.apply();
    }, { signal });

    this.querySelector('[data-sort]')?.addEventListener('change', (e) => this.setSort(e.target.value), { signal });
    this.querySelectorAll('[data-sort-radios] input').forEach((r) =>
      r.addEventListener('change', () => { this.setSort(r.value); this.closeSheets(); }, { signal })
    );

    // Mobile sheets
    this.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-open]');
      if (opener) this.querySelector(`dialog[data-sheet="${opener.dataset.open}"]`)?.showModal();
      if (e.target.closest('[data-apply]')) this.applyFilters();
    }, { signal });
    this.querySelectorAll('dialog').forEach((d) =>
      d.addEventListener('click', (e) => { if (e.target === d) d.close(); }, { signal })
    );

    this.setupFilterSheet(signal);
    this.apply();
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  closeSheets() {
    this.querySelectorAll('dialog[open]').forEach((d) => d.close());
  }

  setSort(value) {
    this.state.sort = value;
    const select = this.querySelector('[data-sort]');
    if (select) select.value = value;
    this.querySelectorAll('[data-sort-radios] input').forEach((r) => (r.checked = r.value === value));
    this.apply();
  }

  apply() {
    const { query, trending, sort } = this.state;
    const sorted = [...this.items].sort((a, b) => {
      if (sort === 'az') return a.dataset.name.localeCompare(b.dataset.name);
      if (sort === 'za') return b.dataset.name.localeCompare(a.dataset.name);
      if (sort === 'newest') return Number(b.dataset.id) - Number(a.dataset.id);
      return Number(b.dataset.pop) - Number(a.dataset.pop);
    });
    let visible = 0;
    const fragment = document.createDocumentFragment();
    fragment.append(...this.fixed);
    for (const item of sorted) {
      const show = (!query || item.dataset.search.includes(query)) && (!trending || item.hasAttribute('data-trending'));
      item.hidden = !show;
      if (show) visible++;
      fragment.append(item);
    }
    this.grid.append(fragment);
    if (this.count) this.count.textContent = `${visible} ${visible === 1 ? 'Note' : 'Notes'}`;
    if (this.empty) this.empty.hidden = visible > 0;
  }

  /* ----- Mobile "Filter Notes": family → sub-category, then navigate ----- */
  setupFilterSheet(signal) {
    this.familySelect = this.querySelector('[data-family-select]');
    this.catSelect = this.querySelector('[data-cat-select]');
    this.applyButton = this.querySelector('[data-apply]');
    if (!this.familySelect || !this.catSelect) return;
    try {
      this.categories = JSON.parse(this.querySelector('[data-categories]')?.textContent || '[]');
      this.notesHome = JSON.parse(this.querySelector('[data-notes-home]')?.textContent || '"/pages/find-your-notes"');
    } catch {
      this.categories = [];
      this.notesHome = '/pages/find-your-notes';
    }
    this.fillCategories(this.catSelect.dataset.current || '');
    this.familySelect.addEventListener('change', () => { this.fillCategories(''); this.updateApplyLabel(); }, { signal });
    this.catSelect.addEventListener('change', () => this.updateApplyLabel(), { signal });
    this.updateApplyLabel();
  }

  fillCategories(selected) {
    const family = this.familySelect.value;
    const options = [`<option value="">${family ? 'All' : 'All Sub Categories'}</option>`];
    for (const c of this.categories) {
      if (family && c.family !== family) continue;
      options.push(`<option value="${c.handle}"${c.handle === selected ? ' selected' : ''}>${c.name.replace(/</g, '&lt;')}</option>`);
    }
    this.catSelect.innerHTML = options.join('');
  }

  updateApplyLabel() {
    const n = (this.familySelect.value ? 1 : 0) + (this.catSelect.value ? 1 : 0);
    this.applyButton.textContent = n ? `Apply ${n} Filter${n > 1 ? 's' : ''}` : 'Show All Notes';
  }

  applyFilters() {
    const cat = this.categories.find((c) => c.handle === this.catSelect.value);
    const familyOption = this.familySelect.selectedOptions[0];
    location.href = cat?.url || familyOption?.dataset.url || this.notesHome;
  }
}

if (!customElements.get('olfacy-notes')) {
  customElements.define('olfacy-notes', OlfacyNotes);
}
